<template>
  <v-card v-if="rows.length" class="mb-4" outlined>
    <v-toolbar :color="onlyRows ? 'red darken-1' : 'amber darken-2'" dark dense flat>
      <v-toolbar-title class="subtitle-1">
        {{ onlyRows ? 'คำขอที่ต้องตัดสินก่อนยืนยันแผน' : 'คำขอรอยืนยัน' }} ({{ rows.length }})
      </v-toolbar-title>
    </v-toolbar>
    <v-list dense>
      <v-list-item v-for="r in rows" :key="r.booking.id" three-line>
        <v-list-item-content>
          <v-list-item-title>
            {{ r.booking.name || r.booking.custName || '-' }} · {{ r.booking.telephone || '-' }}
            · {{ RJ.refText(r.booking) }}
          </v-list-item-title>
          <v-list-item-subtitle>
            <template v-if="r.booking.proposal.kind === 'move'">
              {{ r.booking.date }} {{ slotLabel(r.booking.slot) }}
              <v-icon x-small>mdi-arrow-right</v-icon>
              {{ r.booking.proposal.date }} {{ slotLabel(r.booking.proposal.slot) }}
            </template>
            <template v-else-if="r.booking.fleet === 'large'">
              ขอยกเลิก (รถใหญ่ · {{ r.booking.upTripId ? 'รอบ ' + r.booking.date : 'รอจัดรอบ' }})
            </template>
            <template v-else>ขอยกเลิก (วันที่ {{ r.booking.date }} {{ slotLabel(r.booking.slot) }})</template>
            · {{ reasonText(r.booking.proposal.reason) }}{{ r.booking.proposal.note ? ` — ${r.booking.proposal.note}` : '' }}
          </v-list-item-subtitle>
          <v-list-item-subtitle>
            ขอโดย {{ $personName(r.booking.proposal.by) }} · {{ when(r.booking.proposal.at) }}
            <v-chip v-if="r.state === 'expired'" x-small color="grey" dark class="ml-1">หมดอายุ</v-chip>
            <v-chip v-if="r.state === 'rejectedUntold'" x-small color="red" dark class="ml-1">
              ปฏิเสธแล้ว — ยังไม่ได้แจ้งลูกค้า ({{ r.booking.proposal.rejectReason }})
            </v-chip>
            <v-chip v-if="r.booking.proposal.planned && r.state === 'pending'" x-small color="orange" dark class="ml-1">
              วางแผนรถแล้ว
            </v-chip>
          </v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action class="flex-row align-center">
          <v-btn v-if="r.decide && r.state === 'pending'" small color="green" dark class="mr-1"
                 :loading="busy === r.booking.id" @click="confirm(r)">ยืนยัน</v-btn>
          <v-btn v-if="r.decide && r.state === 'pending'" small outlined color="error" class="mr-1"
                 :disabled="busy === r.booking.id" @click="openReject(r)">ปฏิเสธ</v-btn>
          <v-btn v-if="r.withdraw" small text :disabled="busy === r.booking.id" @click="withdraw(r)">
            {{ r.state === 'expired' ? 'ปิดคำขอ' : 'ถอนคำขอ' }}
          </v-btn>
          <v-btn v-if="r.told" small color="red" dark :loading="busy === r.booking.id" @click="told(r)">
            แจ้งลูกค้าแล้ว
          </v-btn>
        </v-list-item-action>
      </v-list-item>
    </v-list>

    <v-dialog v-model="reject.open" max-width="440" persistent>
      <v-card>
        <v-toolbar color="error" dark dense flat><v-toolbar-title>ปฏิเสธคำขอ</v-toolbar-title></v-toolbar>
        <v-card-text class="pt-4" v-if="reject.row">
          <div class="body-2 mb-2">
            โทรแจ้งลูกค้า {{ reject.row.booking.name || reject.row.booking.custName || '' }}
            <strong>{{ reject.row.booking.telephone || '-' }}</strong><br>
            ลูกค้าได้รับแจ้งไว้ว่า:
            {{ reject.row.booking.proposal.kind === 'move'
              ? `เลื่อนเป็น ${reject.row.booking.proposal.date} ช่วง${slotLabel(reject.row.booking.proposal.slot)}`
              : 'ขอยกเลิกการจัดส่ง' }}
          </div>
          <v-text-field v-model="reject.reason" label="เหตุผลที่ปฏิเสธ" color="error" hide-details="auto" />
          <v-checkbox v-model="reject.told" label="แจ้งลูกค้าแล้ว" color="error" hide-details />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="!!busy" @click="reject.open = false">ปิด</v-btn>
          <v-btn color="error" dark :loading="!!busy" :disabled="!reject.reason" @click="doReject">ปฏิเสธ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
// คำขอรอยืนยัน -- the morning side of propose and confirm. Rows come from the
// open proposals App.vue keeps in Vuex; which of them this user sees, and what
// they may do to each, is proposalRules.panelRows.
import R from '@/components/bookingRules'
import X from '@/components/proposalRules'
import RJ from '@/components/returnJob'
import PS from '@/components/proposalService'
import { reportError } from '@/components/report'
import { pushBookDateToSap, bookDateFailedNote } from '@/components/sapBookDate'

export default {
  name: 'proposalPanel',
  props: {
    // dispatchPlan passes the proposals blocking its date; unset, the panel
    // shows everything this user may act on.
    onlyRows: { type: Array, default: null }
  },
  created () {
    this.RJ = RJ
  },
  data () {
    return { busy: '', reject: { open: false, row: null, reason: '', told: false } }
  },
  computed: {
    ctx () {
      return {
        role: this.$store.state.status,
        identities: this.$store.getters.identities,
        user: this.$store.state.user || '',
        profiles: this.$store.state.profiles,
        today: R.todayStr()
      }
    },
    rows () {
      return X.panelRows(this.onlyRows || this.$store.state.proposals, this.ctx)
    },
    webPath () { return this.$store.getters.webPath }
  },
  methods: {
    slotLabel (s) { return R.SLOT_LABEL_TH[s] || s || '' },
    reasonText (v) { return (R.MOVE_REASONS.find(r => r.value === v) || {}).text || v || '-' },
    when (iso) { return String(iso || '').slice(0, 16).replace('T', ' ') },
    // `failText(e)` may replace the refusal's usual text (null keeps it).
    async act (row, fn, done, failText) {
      this.busy = row.booking.id
      try {
        const r = await fn()
        // `color` may be a function of the result: a write-back to SAP that
        // failed turns an otherwise plain success into something to read.
        const color = typeof done === 'object' && typeof done.color === 'function' ? done.color(r) : (done && done.color)
        if (done) this.$root.$message(done.title, done.text(r), { type: 'alert', color: color || 'success', width: 480 })
        this.$emit('changed')
        return true
      } catch (e) {
        if (!e.reason) reportError('proposal action failed', e)
        this.$root.$message('ทำรายการไม่สำเร็จ', (failText && failText(e)) || (e.reason ? R.errorMessage(e.reason) : 'โปรดลองใหม่อีกครั้ง'),
          { type: 'alert', color: 'error', width: 420 })
        return false
      } finally {
        this.busy = ''
      }
    },
    // An up-country order on a sent trip is refused here: it leaves through
    // รถใหญ่ต่างจังหวัด's ยกเลิกออเดอร์, which also takes it off the sheet and
    // closes this request, so the refusal says where to go.
    confirm (row) {
      this.act(row, async () => {
        const r = await PS.confirmProposal(row.booking.id, this.ctx)
        // The booking moved or ended here, so SAP's U_KSK_BookDate has to agree:
        // otherwise Kiosk Online Shop keeps promising the old date, or a date
        // for a booking that no longer exists. After the decision, never
        // instead of it -- the same order sapPin keeps on the booking pages.
        const sapFailed = await pushBookDateToSap(this.webPath, {
          soNo: row.booking.soNo,
          rtNo: row.booking.rtNo || '',
          date: r.date,
          status: r.kind === 'cancel' ? 'cancelled' : 'booked'
        })
        return Object.assign({}, r, { sapFailed })
      }, {
        title: 'ยืนยันคำขอแล้ว',
        // r.planned, not the proposal's own flag: a booking can be planned while
        // the request waits, and the text below already reads the result's.
        color: r => (r.planned || (r.sapFailed || []).length) ? 'warning' : 'success',
        text: r => [
          r.kind === 'cancel' ? 'ยกเลิกการจองเรียบร้อยแล้ว' : `ย้ายเป็นวันที่ ${r.date} ช่วง${this.slotLabel(r.slot)} เรียบร้อยแล้ว`,
          r.planned ? `ต้องวางแผนรถใหม่${r.kind === 'move' ? `ทั้งวันที่ ${r.from} และ ${r.date}` : `วันที่ ${r.from}`}` : '',
          bookDateFailedNote(r.sapFailed || [], { cleared: r.kind === 'cancel' })
        ].filter(Boolean)
      }, e => (e && (e.reason === 'DISPATCHED' || e.reason === 'UP_ORDER_SENT') && row.booking.fleet === 'large')
        // Only staff can act on รถใหญ่ต่างจังหวัด; anyone else deciding is sent to them.
        ? (R.isStaffRole(this.ctx.role)
            ? 'ออเดอร์นี้อยู่ในรอบรถใหญ่ที่ส่งเข้าจัดรถแล้ว — ยกเลิกที่หน้า รถใหญ่ต่างจังหวัด (ปุ่ม ยกเลิกออเดอร์) คำขอนี้จะถูกยืนยันให้อัตโนมัติ'
            : 'ออเดอร์นี้ส่งเข้าจัดรถแล้ว โปรดแจ้งฝ่ายจัดส่ง')
        : null)
    },
    openReject (row) {
      this.reject = { open: true, row, reason: '', told: false }
    },
    async doReject () {
      const ok = await this.act(this.reject.row, () => PS.rejectProposal(this.reject.row.booking.id,
        Object.assign({ reason: this.reject.reason, customerTold: this.reject.told }, this.ctx)))
      if (ok) this.reject.open = false
    },
    withdraw (row) { this.act(row, () => PS.withdrawProposal(row.booking.id, this.ctx)) },
    told (row) { this.act(row, () => PS.markCustomerTold(row.booking.id, this.ctx)) }
  }
}
</script>
